import { pdf } from "@react-pdf/renderer";
import { ShipmentPdf } from "components/Pdfs/ShipmentPdf/ShipmentPdf";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parsedJsonString, sortProductSizes } from "utils/UtilityFuncs";

export default function SaveAndSendShipmentEmail({
  shipment,
  trackingNo,
  products,
  handleSaveTrackingNo,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [locationLogo, setLocationLogo] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [productsListWithImages, setProductsListWithImages] = useState(null);
  const [enrichedShipment, setEnrichedShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [userSmtp, setUserSmtp] = useState(null);
  const [smtpMessage, setSmtpMessage] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [modalSubject, setModalSubject] = useState("");
  const [modalEmailBody, setModalEmailBody] = useState("");
  const [emailTemplates, setEmailTemplates] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [modalProductsBySize, setModalProductsBySize] = useState(null);

  /*   All Functions
   ********************************************* */
  const getShippingMethodsAndCouriers = async () => {
    try {
      // Fetch shipping methods and courier services
      const [shippingMethodsRes, courierServicesRes] = await Promise.all([
        API.get("/shipmentmethods", { headers: authHeader() }),
        API.get("/courier-services", { headers: authHeader() }),
      ]);

      const shippingMethods = shippingMethodsRes.data || [];
      const courierServices = courierServicesRes.data || [];

      // Find the matching shipping method and courier service
      const shippingMethod = shippingMethods.find(
        (method) => method.id === shipment?.shippingMethodId,
      );
      const courierService = courierServices.find(
        (service) => service.id === shipment?.courierServiceId,
      );

      // Create enriched shipment object with related data
      const enriched = {
        ...shipment,
        shippingMethod: shippingMethod || null,
        courierService: courierService || null,
      };

      setEnrichedShipment(enriched);
      return enriched;
    } catch (error) {
      console.error(
        "Error fetching shipping methods or courier services:",
        error,
      );
      // Return shipment as-is if fetch fails
      setEnrichedShipment(shipment);
      return shipment;
    }
  };

  const getLocationLogo = async () => {
    try {
      // Get customer's locationId
      const locationId = shipment?.customer?.locationId;
      if (!locationId) {
        console.log("No locationId found for customer");
        return null;
      }

      // Fetch all locations
      const { data: locationsData } = await API.get("/locations", {
        headers: authHeader(),
      });

      // Find the location that matches the customer's locationId
      const location = locationsData?.find((loc) => loc.id === locationId);

      if (!location || !location.pdfLogo) {
        console.log("No location or pdfLogo found for locationId:", locationId);
        return null;
      }

      // Fetch the actual image using the pdfLogo path
      const { data: imageData } = await API.post(
        "/design-request/get-image",
        { path: location.pdfLogo },
        {
          headers: authHeader(),
        },
      );

      setLocationLogo(imageData);
      return { logo: imageData, location };
    } catch (error) {
      console.error("Error fetching location logo:", error);
      return null;
    }
  };

  const getAllProductsImages = async (productList) => {
    try {
      setLoading(true);
      const updatedProducts = await Promise.all(
        productList?.map(async (item) => {
          // Flatten product data - merge WOProduct fields to root level
          const flattenedItem = {
            ...item,
            // If WOProduct exists, merge its fields at root level
            productType: item?.productType || item?.WOProduct?.productType,
            productTypes: item?.productTypes || item?.WOProduct?.productTypes,
            productStyle: item?.productStyle || item?.WOProduct?.productStyle,
            productDescription:
              item?.productDescription || item?.WOProduct?.productDescription,
            productColor: item?.productColor || item?.WOProduct?.productColor,
            productFabric:
              item?.productFabric || item?.WOProduct?.productFabric,
            notes: item?.notes || item?.WOProduct?.notes,
          };

          const productImage =
            item?.productImage || item?.WOProduct?.productImage;
          if (productImage) {
            try {
              const { data } = await API.post(
                "/design-request/get-image",
                { path: productImage },
                {
                  headers: authHeader(),
                },
              );
              return {
                ...flattenedItem,
                productImage: data,
                WOProduct: item?.WOProduct
                  ? {
                      ...item.WOProduct,
                      productImage: data,
                    }
                  : undefined,
              };
            } catch (error) {
              console.error("Error fetching product image:", error);
              return flattenedItem;
            }
          }
          return flattenedItem;
        }),
      );
      setProductsListWithImages(updatedProducts);
      setLoading(false);
      return updatedProducts;
    } catch (error) {
      console.error("Error in getAllProductsImages:", error);
      setLoading(false);
      return productList;
    }
  };

  const checkUserLocationSmtp = useCallback(async () => {
    const locationId = shipment?.customer?.locationId;
    if (!locationId) {
      setUserSmtp(null);
      setSmtpMessage(
        "No brand found for this shipment. Please set brand SMTP.",
      );
      return;
    }

    try {
      const { data } = await API.post(
        "/user/smtp/check",
        {
          locationId,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success && data?.smtpEmail) {
        setUserSmtp({ smtpEmail: data?.smtpEmail });
        setSmtpMessage("");
      } else {
        setUserSmtp(null);
        setSmtpMessage(
          data?.message ||
            "No SMTP is configured for this brand. Please add SMTP settings.",
        );
      }
    } catch (error) {
      setUserSmtp(null);
      setSmtpMessage(
        error?.message || "No SMTP is configured for this brand.",
      );
      dispatch(
        setNotification({
          message: error?.message,
          type: "error",
        }),
      );
    }
  }, [dispatch, shipment?.customer?.locationId]);

  const preparePdfData = async () => {
    setLoading(true);
    const [enriched, locationData, productsWithImages] = await Promise.all([
      getShippingMethodsAndCouriers(),
      getLocationLogo(),
      getAllProductsImages(products),
      checkUserLocationSmtp(),
    ]);
    setEnrichedShipment(enriched);
    if (locationData) {
      setLocationLogo(locationData.logo);
      setLocationInfo(locationData.location);
    }
    setProductsListWithImages(productsWithImages);
    setLoading(false);
    setShowSaveButton(true);
  };

  const handleSaveAndEmail = async () => {
    try {
      if (!shipment.customerId || !trackingNo) {
        alert(
          "Shipment must have a customer and tracking number to send email.",
        );
        return;
      }

      if (!userSmtp?.smtpEmail) {
        return;
      }

      let emailTo = shipment?.customer?.accountPayableEmail || null;
      if (!emailTo) {
        alert(
          "Customer does not have a default email to send shipment details.",
        );
        return;
      }

      let productsBySize = products?.reduce((acc, item) => {
        let sizesArray = sortProductSizes(item?.productSizes || "[]");
        // sort sizesArray by sortId
        sizesArray.sort((a, b) => (a.sortId || 0) - (b.sortId || 0));
        sizesArray.forEach((sizeObj) => {
          let sizeKey = sizeObj.size;
          let totalQty = parseInt(sizeObj.quantity) || 0;
          let shippedQty = parseInt(sizeObj.shipmentQty) || 0;
          let pendingQty = totalQty - shippedQty;
          let price = parseFloat(sizeObj.price) || 0;
          let total = price * totalQty;

          let productItem = {
            sProductId: item.sProductId,
            woId: item.woId,
            productType: item?.WOProduct?.productTypes?.productTypes || "",
            styleId: item?.WOProduct?.productStyle || "",
            color: item?.WOProduct?.productColor || "",
            desc: item?.WOProduct?.productDescription || "",
            fabric: item?.WOProduct?.productFabric || "",
            size: sizeKey,
            sortId: sizeObj.sortId || 0,
            quantity: totalQty,
            shippedQty: shippedQty,
            pendingQty: pendingQty,
            price: price,
            total: total,
          };
          acc.push(productItem);
        });
        return acc;
      }, []);

      // console.log("productsBySize", productsBySize);

      // return;
      // Instead of directly sending, open modal to ask for subject/body
      // Prepare default subject/body
      setModalSubject(`Shipment #${shipment?.shipmentNumber || ""}`);
      setModalEmailBody("");
      setModalProductsBySize(productsBySize);
      // load templates once
      try {
        const { data } = await API.get("/emailformat", {
          headers: authHeader(),
        });
        if (data?.type === "success" || data?.success) {
          setEmailTemplates(data.data || data?.data || []);
        }
      } catch (err) {
        // ignore
      }
      setShowEmailModal(true);
    } catch (error) {
      console.error("Error sending email:", error);
      dispatch(
        setNotification({
          message: error.message,
          type: "error",
        }),
      );
    } finally {
      setIsSending(false);
    }
  };

  const normalizeEmailHtml = (html) => {
    if (!html) return "";
    let cleaned = html.replace(/\u00A0/g, " ");
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, "text/html");
      doc.querySelectorAll("p").forEach((p) => {
        const textOnly = (p.textContent || "").replace(/\s/g, "");
        const hasOnlyBr =
          p.querySelectorAll("br").length > 0 && textOnly === "";
        if (textOnly === "" && !hasOnlyBr) {
          p.remove();
        } else {
          p.style.margin = "0";
        }
      });
      doc.body.innerHTML = doc.body.innerHTML.replace(
        /(<br\s*\/?>\s*){2,}/gi,
        "<br />",
      );
      return doc.body.innerHTML;
    } catch (err) {
      return cleaned;
    }
  };

  const replaceTemplateVariables = (val) => {
    if (!val) return "";
    const shipmentId = shipment?.shipmentId || "";
    const tracking = trackingNo || "";
    const customerName =
      shipment?.customer?.customerName ||
      `${shipment?.customer?.firstName || ""} ${shipment?.customer?.lastName || ""}`.trim();

    const getContactName = (obj) =>
      `${obj?.firstName || ""} ${obj?.lastName || ""}`.trim();
    const contactName = shipment?.customer
      ? typeof shipment.customer === "string"
        ? getContactName(JSON.parse(shipment.customer))
        : getContactName(shipment.customer)
      : "";

    // helper: replace token name robustly (handles extra spaces and HTML-escaped tokens)
    const replaceToken = (text, tokenName, value) => {
      if (!text) return text;
      const parts = tokenName
        .split(/\s+/)
        .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      const namePattern = parts.join("\\s*");
      const re = new RegExp(
        `(&lt;&lt;|<<)\\s*${namePattern}\\s*(&gt;&gt;|>>)`,
        "gi",
      );
      return text.replace(re, value);
    };

    let updated = val;
    updated = replaceToken(updated, "Shipment ID", shipmentId);
    updated = replaceToken(updated, "Tracking No", tracking);
    updated = replaceToken(updated, "Customer Name", customerName);
    if (contactName) {
      updated = replaceToken(updated, "Contact Name", contactName);
    }

    return updated;
  };

  const confirmAndSend = async () => {
    setShowEmailModal(false);
    setIsSending(true);
    try {
      // Generate PDF blob
      const blob = await pdf(
        <ShipmentPdf
          shipment={enrichedShipment}
          productsList={productsListWithImages}
          locationLogo={locationLogo}
          locationInfo={locationInfo}
        />,
      ).toBlob();

      const formData = new FormData();
      formData.append(
        "file",
        blob,
        `Shipment_${shipment?.shipmentNumber || "Details"}.pdf`,
      );
      formData.append("shipmentId", shipment?.shipmentId);
      formData.append("emailTo", shipment?.customer?.accountPayableEmail || "");
      formData.append("products", JSON.stringify(modalProductsBySize || []));
      formData.append("customerId", shipment?.customer?.customerId || "");
      formData.append("locationId", shipment?.customer?.locationId || "");
      formData.append("trackingNo", trackingNo || "");
      formData.append("subject", modalSubject || "");
      formData.append("emailBody", normalizeEmailHtml(modalEmailBody || ""));

      const { data } = await API.post(
        "/sales/shipments/save-tracking",
        formData,
        {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        },
      );

      if (data.success) {
        dispatch(
          setNotification({
            message: "Shipment email sent successfully",
            type: "success",
          }),
        );
      } else {
        throw new Error(data.message || "Failed to send shipment email");
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || err?.response?.data?.message,
          type: "error",
        }),
      );
    } finally {
      setIsSending(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      preparePdfData();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      {showSaveButton && (
        <>
          {!userSmtp?.smtpEmail && smtpMessage && (
            <div className="text-danger fw-bold mb-2">{smtpMessage}</div>
          )}
          <button
            onClick={handleSaveTrackingNo}
            className="btn btn-success btn-sm me-2"
          >
            Save
          </button>
          <button
            className="btn btn-info btn-sm"
            onClick={handleSaveAndEmail}
            disabled={
              loading ||
              isSending ||
              !userSmtp?.smtpEmail ||
              !enrichedShipment ||
              !productsListWithImages
            }
          >
            {isSending ? "Sending..." : "Save & Email"}
          </button>
        </>
      )}
      <Modal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        size="lg"
      >
        <Modal.Body className="bg-white">
          <h4 className="h4">Send Shipment Email</h4>
          <div className="mb-2">
            <label className="form-label">Subject</label>
            <input
              className="form-control"
              value={modalSubject}
              onChange={(e) => setModalSubject(e.target.value)}
            />
          </div>
          <div className="mb-2 d-flex justify-content-between align-items-center">
            <label className="form-label">Email Body</label>
            {/* Hidden per request -- Email Template feature hidden in Settings, to be deleted later */}
            <div className="d-none">
              <button
                className="btn btn-sm btn-secondary"
                onClick={async () => {
                  try {
                    const { data } = await API.get("/emailformat", {
                      headers: authHeader(),
                    });
                    if (data?.type === "success" || data?.success) {
                      setEmailTemplates(data.data || data?.data || []);
                    }
                  } catch (err) {
                    // ignore
                  }
                  setShowTemplateModal(true);
                }}
              >
                Select Template
              </button>
            </div>
          </div>
          <div className="mb-2">
            <ReactQuill
              theme="snow"
              value={modalEmailBody}
              onChange={setModalEmailBody}
            />
          </div>

          {/* Templates are selected from a separate modal */}

          <div className="text-end mt-3">
            <button
              className="btn btn-secondary me-2"
              onClick={() => setShowEmailModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={confirmAndSend}
              disabled={isSending}
            >
              Send
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        size="md"
        contentClassName="bg-light shadow-sm"
      >
        <Modal.Body className="bg-white">
          <h5>Select Template</h5>
          <div className="d-flex flex-column mt-2">
            {emailTemplates ? (
              emailTemplates.map((t) => (
                <div
                  key={t.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <div>{t.title}</div>
                  <div>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => {
                        setModalEmailBody(
                          replaceTemplateVariables(t.format || ""),
                        );
                        setModalSubject((prev) =>
                          t.subject
                            ? replaceTemplateVariables(t.subject)
                            : prev,
                        );
                        setShowTemplateModal(false);
                      }}
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div>Loading templates...</div>
            )}
          </div>
          <div className="text-end mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => setShowTemplateModal(false)}
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
